/**
 * Deal Service
 * Handles all deal/subscription-related operations and API calls
 *
 * All deal balances are tracked by organization_id, not user_id.
 * All users within an organization share the same deal pool.
 */

import { supabase } from '@/integrations/supabase/client';
import { supabaseUntyped } from '@/integrations/supabase/typed-client';
import {
  type DealStatus,
  type DealStatusResponse,
  type DealConsumptionLog,
  type SubscriptionHistoryEvent,
  type SubscriptionPlan,
  type SubscriptionPlanRow,
  InsufficientDealsError,
  NoActiveSubscriptionError,
  SubscriptionExpiredError,
  mapDealStatusResponse,
  mapSubscriptionPlanRow,
} from '@/types/deals';

async function getCurrentOrganizationId(): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const organizationId = user.app_metadata?.organization_id as string | undefined;

  if (!organizationId) {
    throw new Error('User organization not found');
  }

  return organizationId;
}

/**
 * Get the organization's deal status (subscription info + deal usage)
 * @param organizationId - Optional organization ID for admin lookups
 */
export async function getDealStatus(organizationId?: string): Promise<DealStatus> {
  if (organizationId) {
    const { data, error } = await supabaseUntyped.rpc('admin_get_org_deal_status', {
      p_organization_id: organizationId,
    });

    if (error) {
      console.error('Error fetching org deal status:', error);
      throw error;
    }

    const rows = (data as DealStatusResponse[] | null) ?? [];
    return mapDealStatusResponse(rows[0] ?? null);
  }

  const { data, error } = await supabaseUntyped.rpc('get_current_org_deal_status');

  if (error) {
    console.error('Error fetching deal status:', error);
    throw error;
  }

  const rows = (data as DealStatusResponse[] | null) ?? [];
  return mapDealStatusResponse(rows[0] ?? null);
}

/**
 * Consume a deal for a transaction
 * @param transactionId - Optional transaction ID to associate with this consumption
 * @param organizationId - Optional organization ID. If not provided, uses current user's org.
 */
export async function consumeDeal(
  transactionId?: number,
  organizationId?: string
): Promise<void> {
  const args: Record<string, unknown> = {
    p_transaction_id: transactionId ?? null,
    p_deals_to_consume: 1,
  };

  if (organizationId) {
    args.p_organization_id = organizationId;
  }

  const { error } = await supabaseUntyped.rpc('consume_deal', args);

  if (error) {
    const message = typeof error.message === 'string' ? error.message : undefined;

    if (message?.includes('Insufficient deals')) {
      const match = message.match(/need (\d+), have (\d+)/);
      if (match) {
        throw new InsufficientDealsError(parseInt(match[1]), parseInt(match[2]));
      }
      throw new InsufficientDealsError(1, 0);
    }

    if (message?.includes('No subscription found') || message?.includes('No active subscription')) {
      throw new NoActiveSubscriptionError();
    }

    if (message?.includes('Billing period has expired')) {
      const dateMatch = message.match(/expired on (\d{4}-\d{2}-\d{2})/);
      throw new SubscriptionExpiredError(dateMatch?.[1]);
    }

    throw error;
  }
}

/**
 * Check if organization has sufficient deals
 * @param required - Number of deals required
 */
export async function hasSufficientDeals(required: number = 1): Promise<boolean> {
  try {
    const status = await getDealStatus();
    return status.dealsRemaining >= required;
  } catch (error) {
    console.error('Error checking deal availability:', error);
    return false;
  }
}

/**
 * Get subscription plans from the database
 */
export async function getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) {
    throw error;
  }

  return ((data ?? []) as unknown as SubscriptionPlanRow[]).map(mapSubscriptionPlanRow);
}

/**
 * Get deal consumption history
 */
export async function getDealConsumptionHistory(
  organizationId?: string,
  options: { limit?: number; offset?: number } = {}
): Promise<{ logs: DealConsumptionLog[]; total: number }> {
  if (!organizationId) {
    organizationId = await getCurrentOrganizationId();
  }

  const { limit = 10, offset = 0 } = options;

  const { data, error, count } = await supabase
    .from('deal_consumption_logs')
    .select('*', { count: 'exact' })
    .eq('organization_id', organizationId)
    .order('consumed_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    throw error;
  }

  return {
    logs: (data ?? []) as unknown as DealConsumptionLog[],
    total: count || 0,
  };
}

/**
 * Get subscription history (billing events)
 */
export async function getSubscriptionHistory(
  organizationId?: string,
  options: { limit?: number; offset?: number } = {}
): Promise<{ events: SubscriptionHistoryEvent[]; total: number }> {
  if (!organizationId) {
    organizationId = await getCurrentOrganizationId();
  }

  const { limit = 10, offset = 0 } = options;

  const { data, error, count } = await supabase
    .from('subscription_history')
    .select('*', { count: 'exact' })
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    throw error;
  }

  return {
    events: (data ?? []) as unknown as SubscriptionHistoryEvent[],
    total: count || 0,
  };
}

/**
 * Format deal count for display
 */
export function formatDealCount(used: number, total: number): string {
  return `${used}/${total}`;
}

/**
 * Get deal usage percentage
 */
export function getDealUsagePercentage(used: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((used / total) * 100);
}

/**
 * Get color class based on deal remaining percentage
 */
export function getDealStatusColor(remaining: number, total: number): 'green' | 'orange' | 'red' {
  if (total === 0) return 'red';
  const remainingPercent = (remaining / total) * 100;
  if (remainingPercent > 50) return 'green';
  if (remainingPercent >= 25) return 'orange';
  return 'red';
}

/**
 * Lifetime totals for the caller's organization. Restored on the
 * DealHistory page in Slice I3 to replace the lifetime stat cards that
 * existed on the legacy TokenHistory page.
 */
export interface LifetimeDealStats {
  /** Sum of deals_consumed across every row in deal_consumption_logs. */
  lifetimeDealsUsed: number;
  /**
   * Sum of amount_charged across billing-charge rows in subscription_history
   * (subscribed / renewed / upgraded), minus any refunded rows.
   */
  lifetimeBilled: number;
}

/**
 * Get lifetime deal usage and billing totals for the caller's organization.
 *
 * Aggregates client-side from deal_consumption_logs and subscription_history.
 * Both reads are RLS-scoped to the caller's organization_id; row counts
 * per org are small (each period adds 4-36 deal rows + a single billing
 * row) so a select + reduce is fine without a server-side aggregate RPC.
 * If row counts ever balloon this should become a SECURITY DEFINER RPC.
 */
export async function getLifetimeDealStats(): Promise<LifetimeDealStats> {
  const [consumption, billing] = await Promise.all([
    supabase.from('deal_consumption_logs').select('deals_consumed'),
    supabase
      .from('subscription_history')
      .select('event_type, amount_charged')
      .in('event_type', ['subscribed', 'renewed', 'upgraded', 'refunded']),
  ]);

  if (consumption.error) {
    throw new Error(`Failed to load lifetime deal usage: ${consumption.error.message}`);
  }
  if (billing.error) {
    throw new Error(`Failed to load lifetime billing: ${billing.error.message}`);
  }

  const lifetimeDealsUsed = (consumption.data ?? []).reduce(
    (sum: number, row: { deals_consumed?: number | null }) => sum + (row.deals_consumed ?? 0),
    0
  );

  const lifetimeBilled = (billing.data ?? []).reduce(
    (sum: number, row: { event_type: string; amount_charged?: number | null }) => {
      const amount = Number(row.amount_charged ?? 0);
      // Refund rows store the refund amount as a positive amount_charged;
      // subtract them from lifetime billed.
      return row.event_type === 'refunded' ? sum - amount : sum + amount;
    },
    0
  );

  return { lifetimeDealsUsed, lifetimeBilled };
}
